import {mapActions, mapState} from 'vuex';
import TrainingPlayer from './base/training-player';
import Training from "./base/training";
import './base/action-registry';
import './action-registry';
import './resource-registry';
import ResourceFactory from "./base/resource-factory";
import '../../services/boundary/boundary-registry';
import {ActionTypeEnum, ResourceTypeEnum, updateWorkspaceFeatures} from "./base/training-literals";
import _ from "lodash";

export default {
    name: 'TrainingFeature',
    render: () => null,

    data: () => ({
        trainingPlayer: null,
        activeTraining: null
    }),

    computed: {
        ...mapState('training', {
            activeTrainingId: 'activeTrainingId',
            trainingData: 'trainingData',
            trainings: 'trainings'
        }),

        treeState: function () {
            const chapterList = this.trainingData?.chapters?.map(chapter => ({
                id: chapter.id,
                text: chapter.name,
                data: {
                    is_chapter: true
                }
            }));

            return this.trainings ? this.trainings.map(training => ({
                id: training._id,
                children: (training._id === this.activeTrainingId ? chapterList : false),
                text: training.name,
                state: {
                    opened: training._id === this.activeTrainingId
                },
                data: {
                    is_chapter: false
                },
                name: training.name
            })) : [];
        },

        trainingList: function () {
            return this.trainings ? this.trainings.map(training => ({
                id: training._id,
                chapters: getChapters.call(this, training),
                name: training.name,
                state: {
                    opened: training._id === this.activeTrainingId
                },
                data: {
                    is_chapter: false
                },
                callback: this.setActiveTraining
            })) : [];

            function getChapters(training) {
                return training?.chapters?.map(chapter => ({
                    id: chapter.id,
                    name: chapter.name,
                    data: {
                        is_chapter: true
                    },
                    callback: this.playChapter
                }));
            }
        }
    },

    created: function () {
        TrainingTreeController.setTrainingFeature(this);
    },

    destroyed: function () {
        if (this.stepInstructionMenuInstance) {
            this.stepInstructionMenuInstance.stop();
        }
    },

    methods: {
        ...mapActions('training', [
            'loadAvailableTrainings',
            'closeTraining',
            'setActiveTraining',
            'setCustomActiveTraining'
        ]),

        playChapter: async function (chapterId) {
            if (!(wv.effectController.trainingMeeting || isServerLessTrainingUIMeeting())) {
                this.activeTraining = null;
            }

            await this.stopPlayer();
            wv.trainingAuthoringEnabled = false;
            await this._playChapter(chapterId);
            TrainingTreeController.trainingTreeView.updateButtonState(chapterId, false);
            this.trainingPlayer = null;
            wv.trainingAuthoringEnabled = true;
        },

        _playChapter: async function (chapterId) {
            try {
                const training = this.getActiveTraining();
                if (wv.effectController.stoppedTrainingId === training.id) {
                    return;
                }

                TrainingTreeController.trainingTreeView.updateButtonState(chapterId, true);
                if (!(wv.effectController.trainingMeeting || isServerLessTrainingUIMeeting())) {
                    MediaScreenManager.removeAllScreensFromScene();
                    wayfindingManager.removeAllWayfindingLines();
                    externalSignalReceiverManager.stopAllExternalSignalReceiverActions();
                    await training.loadResources();
                }
                await updateMenuForTraining();
                clearAllFeaturesFromAllHandler();

                // check if the training has step Instruction Menu
                let hasStepInstructionMenu = Object.values(training.timelines).some(timeline =>
                    timeline.hasActionType(ActionTypeEnum.STEP_INSTRUCTION_MENU)
                );
                InstructionManager.updateButtonAndToolbarPosition(hasStepInstructionMenu);

                this.trainingPlayer = new TrainingPlayer(training, chapterId);
                await this.trainingPlayer.playModule(chapterId);
                console.log('finished playing chapter: ', training.chapters[chapterId].name);
                this.onChapterEnd();

                InstructionManager.updateButtonAndToolbarPosition(false);
            } catch (error) {
                console.error('Error occurred when playing chapter.', error.message);
            }
        },

        getActiveTraining: function () {
            if (!this.activeTrainingId) {
                return;
            }

            if (!this.activeTraining || this.activeTraining.id !== this.activeTrainingId) {
                const trainingInfo = this.trainings.find(training => training._id === this.activeTrainingId);
                this.activeTraining = new Training(trainingInfo, this.trainingData);
            }

            return this.activeTraining;
        },

        getTrainingData: function () {
            return this.trainingData || {};
        },

        getTrainingInfo: function () {
            return this.trainings.find(training => training._id === this.activeTrainingId);
        },

        stopPlayer: async function (retainState = false) {
            if (this.stepInstructionMenuInstance) {
                await this.stepInstructionMenuInstance.stop();
                this.stepInstructionMenuInstance = null;
            }

            if (retainState && wv.effectController.stoppedTrainingId !== '') {
                updateWorkspaceFeatures();
            }

            if (!this.trainingPlayer) {
                return false;
            }

            await this.trainingPlayer.stopModule(retainState);
            wv.trainingSplashScreen?.dispose();
            const playingChapterId = this.trainingPlayer.currentChapterId;
            TrainingTreeController.trainingTreeView.updateButtonState(playingChapterId, false);
            this.trainingPlayer = null;
            return true;
        },

        getPlayingChapterInfo: function () {
            if (this.trainingPlayer) {
                return {
                    chapterId: this.trainingPlayer.currentChapterId,
                    trainingName: this.trainingPlayer.training.name
                };
            }

        },

        async resetScene(removeModel = false) {
            if (this.activeTraining) {
                await this.stopPlayer();
                this.activeTraining = null;
                await this.closeTraining();
            }

            resetAllSelectionOperation(0);
            updateCatchModelPosition(undefined, false);
            resetCatchAndMoveVolume();
            CatchVolumeFeatureController.onChangeMoveFeature(false);
            CatchVolumeFeatureController.onChangeMoveBodyPrecision(false);
            CADModel.modelArray.forEach(function (child) {
                resetModelPositionAndRotation(child.modelId);
            });
            point_cloud_helper.point_cloud.forEach(function (child) {
                resetModelPositionAndRotation(child.modelId);
            });
            wv.resetRootObjectPositionAndRotation(true, true);
            syncModelLevelExplodeStepCountFromServer(0);

            if (removeModel) {
                wv.hideAndResetAllModel();
            }

            MediaScreenManager.hideAllScreens();
            MediaScreenTreeController.removeAllMediaScreenFromTree();
            wayfindingManager.removeAllWayfindingLines();
            externalSignalReceiverManager.stopAllExternalSignalReceiverActions();
            informationAnnotationFeature.removeAllHotSpotsFromScene();
            imageHotSpotFeature.clearAll360Images();
            videoHotSpotFeature.clearAll360Videos();
        },

        updateStepInstructionMenu: function (stepMenuProps) {
            if (this.stepInstructionMenuInstance) {
                if (this.stepInstructionMenuInstance.actionId !== stepMenuProps.actionId) {
                    console.log('stepInstructionMenuInstance is not the same as the stepMenuProps.actionId');
                    return;
                }

                this.stepInstructionMenuInstance.instructionMenuController?.updateConfigForNonVR({
                    accentColor: rgbToHexColorCode(stepMenuProps.accentColor.r, stepMenuProps.accentColor.g, stepMenuProps.accentColor.b),
                    backgroundColor: rgbToHexColorCode(stepMenuProps.backgroundColor.r, stepMenuProps.backgroundColor.g, stepMenuProps.backgroundColor.b),
                    buttonBackgroundColor: rgbaToHexColorCode(stepMenuProps.buttonBackgroundColor),
                    buttonHoverColor: rgbToHexColorCode(stepMenuProps.buttonHoverColor.r, stepMenuProps.buttonHoverColor.g, stepMenuProps.buttonHoverColor.b),
                    buttonInfo: [],
                    selectedColor: this.stepInstructionMenuInstance.instructionMenuController.selectedColor,
                    textColor: rgbToHexColorCode(stepMenuProps.textColor.r, stepMenuProps.textColor.g, stepMenuProps.textColor.b),
                    title: stepMenuProps.title,
                    text: stepMenuProps.text,
                    opacity: stepMenuProps.opacity,
                    headingFontSize: 30,
                    textFontSize: 18,
                });
            }
        },

        loadServerLessTrainingState: async function (training, stateNames, removeModel, updateCurrentTraining = true, ignoreLockMovementOrientation = false) {
            if (removeModel) {
                StepInstructionScreenManager.remove();
            }

            if (this.stepInstructionMenuInstance) {
                await this.stepInstructionMenuInstance.stop();
                this.stepInstructionMenuInstance = null;
            }

            const waitUntilUserTaskFinished = async (userId, checkInterval = 50, timeout = 10000) => {
                const startTime = Date.now();
                return new Promise((resolve, reject) => {
                    const finished = followUserServiceHelper.movementTask.isUserTaskFinished(userId);

                    if (finished) {
                        return resolve();
                    }

                    const interval = setInterval(() => {
                        const finished = followUserServiceHelper.movementTask.isUserTaskFinished(userId);
                        if (finished) {
                            clearInterval(interval);
                            resolve();
                        } else if (Date.now() - startTime > timeout) {
                            clearInterval(interval);
                            reject(new Error('Timeout while waiting for user movement task to finish'));
                        }
                    }, checkInterval);
                });
            };

            wv.effectController.serverlessEditorLoadingState = true;
            wv.effectController.stoppedTrainingId = '';
            wv.trainingAuthoringEnabled = true;
            wv.effectController.convertPDFToImages = false;

            const getUpdatedTraining = () => {
                const states = [];

                for (let name of stateNames) {
                    const state = training.states.find(state => state.id === name || state.resourceId === name);
                    states.push(state);
                }

                training.chapters = [];
                training.states = states;

                if (training.compatibleWithSimplifiedEditor) {
                    training.timelines = training.timelines.filter(timeline => timeline.name === states[0].name).slice(0, 1);
                    const stepMenu = training.timelines[0].actionSequence?.slice(0, 2).find(action => action.type === ActionTypeEnum.STEP_INSTRUCTION_MENU && action.startTime === 0);
                    if (stepMenu) {
                        stepMenu.buttons.forEach(button => button.disabled = true);
                        training.timelines[0].actionSequence = [stepMenu];
                    }
                } else {
                    training.timelines = [];
                }

                if (!training._id) {
                    training._id = training.id;
                }
                const trainingController = new Training(training, training);

                const updatedResources = {};
                const updatedActionStates = {};

                for (const name of stateNames) {
                    const stateKeys = Object.keys(trainingController.states[name])
                        .filter(key => key !== '__actionStates');

                    stateKeys.forEach(key => {
                        if (key !== 'null') {
                            const value = trainingController.states[name][key];
                            const resourceId = value.instanceId || value.id;
                            updatedResources[key] = trainingController.resources[resourceId] ||
                                Object.values(trainingController.resources)
                                    .find(resource => resource.id === resourceId);
                        }
                    });
                }

                for (const name of stateNames) {
                    const actionStatesContainer = trainingController.states[name].__actionStates;

                    if (actionStatesContainer) {
                        for (const instanceId in actionStatesContainer) {
                            const actionState = actionStatesContainer[instanceId];

                            const resourceId = actionState.instanceId || actionState.id;
                            const resource = trainingController.states[name].__actionStates[resourceId];
                            updatedActionStates[resourceId] = resource;
                        }
                    }
                }

                if (training.compatibleWithSimplifiedEditor) {
                    trainingController.loadingStateWithStepMenu = true;
                    training.loadingStateWithStepMenu = true;
                    for (let id in trainingController.resources) {
                        const resource = trainingController.resources[id];
                        if (resource.type === 'Style') {
                            updatedResources[id] = resource;
                        }
                    }
                }

                trainingController.resources = updatedResources;
                trainingController.actionStates = updatedActionStates;
                return trainingController;
            };

            const trainingController = getUpdatedTraining();

            if (this.trainingPlayer) {
                await this.stopPlayer(!removeModel);
                this.activeTraining = null;
                await this.closeTraining();
            }

            if (removeModel) {
                await this.resetScene(removeModel);
            }

            if (updateCurrentTraining) {
                this.setCustomActiveTraining({id: training._id || training.id, ..._.cloneDeep(training)});
            }

            await trainingController.loadResources(false, true);
            const currentUserId = ParticipantService.user?.id;

            if (currentUserId && followUserServiceHelper.movementTask._tasks.hasOwnProperty(currentUserId) && anyStateHasModels()) {
                try {
                    await waitUntilUserTaskFinished(currentUserId);
                } catch (err) {
                    console.warn("User movement task did not finish in time:", err);
                }
            }

            wv.allVolumesForFaces.visible = true;
            trainingController.prepareStates(stateNames);
            await trainingController.loadStates({
                staticPatchState: !removeModel,
                ignoreLockMovementOrientation: ignoreLockMovementOrientation
            });
            await trainingController.loadActionStates();

            GroupTreeController.refreshTree();
            ModelTreeControllerFor2D.deselectAllNodes();

            sendRemoveModelMessage();
            wv.effectController.serverlessEditorLoadingState = false;

            if (training.compatibleWithSimplifiedEditor) {
                this.stepInstructionMenuInstance = Object.values(trainingController.timelines)[0].actionSequence[0];
                await this.stepInstructionMenuInstance.execute({ training: trainingController });
            }

            function anyStateHasModels() {
                for (let resourceId in trainingController.resources) {
                    if (trainingController.resources[resourceId].type === ResourceTypeEnum.MODEL) {
                        for (let stateId in trainingController.states) {
                            if (trainingController.states[stateId].hasOwnProperty(resourceId)) {
                                return true;
                            }
                        }
                    }
                }

                return false;
            }

            setTimeout(() => {
                // get training again after loading the state
                wv.communicator.socket.communicateWithContainer({
                    message: "getTrainingData"
                });
            }, 0);
        },

        loadServerLessTrainingTimeline: async function (training, timelineName) {
            const chapterName = timelineName + '_chapter';
            wv.effectController.stoppedTrainingId = '';
            wv.effectController.convertPDFToImages = false;

            let chapters = [];
            chapters.push({
                entryTimelines: [timelineName],
                id: chapterName,
                name: chapterName,
            });

            const isTimelineExists = (timelineId) => {
                return allTimelines.find(timeline => timeline.id === timelineId);
            };

            let allTimelines = [];
            const getAllTimelinesInTimeline = (timeline) => {
                if (typeof timeline === 'string') {
                    timeline = JSON.parse(timeline);
                }

                let actionSequence = timeline.actionSequence;
                actionSequence.forEach(action => {
                    if (action.type.toLowerCase() === ActionTypeEnum.LOAD_TIMELINE.toLowerCase()) {
                        action.timelines.forEach(actionTimeline => {
                            const thisTimeline = training.timelines.find(timeline => timeline.id === actionTimeline);
                            if (!isTimelineExists(thisTimeline.id)) {
                                allTimelines.push(thisTimeline);
                                getAllTimelinesInTimeline(thisTimeline);
                            }
                        });
                    }
                });
            };

            const getUpdatedTraining = () => {
                let states = [];
                let timelines = [];

                const timeline = training.timelines.find(timeline => timeline.id === timelineName);
                timelines.push(timeline);

                allTimelines.push(timeline);
                getAllTimelinesInTimeline(timeline);

                const allTimelinesSet = new Set(allTimelines.map(timeline => JSON.stringify(timeline)));
                allTimelines = Array.from(allTimelinesSet).map(str => JSON.parse(str));

                allTimelines.forEach(timeline => {
                    let actionSequence = timeline.actionSequence;
                    actionSequence.forEach(action => {
                        if (action.type.toLowerCase() === ActionTypeEnum.LOAD_STATE.toLowerCase()) {
                            action.states.forEach(actionState => {
                                if (typeof actionState !== "string") {
                                    return;
                                }

                                const thisState = training.states.find(state => state.id === actionState);
                                if (!states.find(state => state.id === thisState.id)) {
                                    states.push(thisState);
                                }
                            });
                        }
                    });
                });

                training.chapters = chapters;
                training.states = states;
                training.timelines = allTimelines;
                training.id = training._id;

                const trainingController = new Training(training, training);
                let keys = [];
                states.forEach(stateData => {
                    let stateKeys = Object.keys(trainingController.states[stateData.id]);
                    keys = [...new Set(keys)];
                    keys = [...new Set([...keys, ...stateKeys])];
                });

                allTimelines.forEach(timeline => {
                    let actionSequence = timeline.actionSequence;
                    actionSequence.forEach(action => {
                        let actionArr = [];
                        const actionType = action.type.toLowerCase();
                        if (actionType === ActionTypeEnum.PLAY_AUDIO.toLowerCase()) {
                            actionArr.push(action.audioId);
                        } else if (actionType === ActionTypeEnum.LOAD_VIEWPOINT.toLowerCase()) {
                            actionArr.push(action.viewpointId);
                        } else if (actionType.includes(ResourceTypeEnum.MEDIA_SCREEN.toLowerCase())) {
                            actionArr.push(action.mediaId);
                        } else if (actionType !== ActionTypeEnum.GLB_ANIMATION.toLowerCase() && actionType.includes(ResourceTypeEnum.ANIMATION)) {
                            actionArr.push(action.animationId);
                        } else if (actionType === ActionTypeEnum.VR_INFORMATION.toLowerCase() || actionType === ActionTypeEnum.COMMAND_ACTION.toLowerCase()) {
                            if (action.hasOwnProperty('styleId')) {
                                actionArr.push(action.styleId);
                            }
                        }

                        keys = [...new Set([...keys, ...actionArr])];
                    });
                });

                keys.push(ResourceTypeEnum.CAMERA);
                keys.push(ResourceTypeEnum.SCENE);
                keys = [...new Set([...keys])];

                training.resources = [];
                keys.forEach(key => {
                    training.resources.push((trainingController.resources[key]));
                });

                return training;
            };

            training = getUpdatedTraining();
            await this.resetScene();
            this.setCustomActiveTraining(training);
            await TrainingTreeController._trainingFeature.playChapter(chapterName);
        },

        compareAndDecomposeStates(trainingData, state1, state2) {
            let resources = trainingData.resources;
            let states = trainingData.states;

            state1 = states.find(state => state.id === state1);
            state2 = states.find(state => state.id === state2);

            let transitionState = {
                resourceStates: []
            };

            for (let key in ResourceFactory.switchObject) {
                transitionState.resourceStates.push(...ResourceFactory.switchObject[key]({}).compareStates(
                    state1.resourceStates.filter(resource => getResourceKey(resource) === key) || {},
                    state2.resourceStates.filter(resource => getResourceKey(resource) === key) || {}
                ));
            }

            let actions = [];

            for (let key in ResourceFactory.switchObject) {
                actions.push(...ResourceFactory.switchObject[key]({}).decompose(
                    transitionState.resourceStates.filter(resource => getResourceKey(resource) === key) || {},
                    true, resources.filter(resource => getResourceKey(resource) === key)
                ));
            }

            // All loadStates action need to be under single action sequence for data consistency
            const loadStateActions = [];
            const otherActions = [];
            actions.forEach(action => {
                if (action.type === 'LoadState') {
                    loadStateActions.push(...action.states[0].resourceStates);
                } else {
                    otherActions.push(action);
                }
            });

            function getResourceKey(resource) {
                let resourceEntry = resources.find(entry => entry.id === resource.id);
                return resourceEntry.type;
            }

            actions = [{
                startTime: 0,
                actionId: generateUUID(),
                type: "LoadState",
                appendState: true,
                loadViewpoint: false,
                patchState: false,
                states: [{
                    resourceStates: loadStateActions
                }]
            }, ...otherActions];

            let startingTime = 0;
            const interval = 100;

            // update startTime based on duration for movement related actions
            actions.forEach(action => {
                action.startTime = startingTime;

                if (action.hasOwnProperty('animateMovement') && action.animateMovement && action.hasOwnProperty('animateTime')) {
                    startingTime += (action.animateTime + interval);
                } else {
                    startingTime += interval;
                }
            });

            return actions;
        },

        onChapterEnd() {
            this.showTrainingEndPrompt();
        },

        showTrainingEndPrompt() {
            if (!wv.effectController.trainingMeeting || userConfirmationManager.isConfirmationMessageShown()) {
                return;
            }

            const onConfirmation = (restart) => {
                if (restart) {
                    window.location.reload();
                    return;
                }

                if (wv.settings?.redirectURL) {
                    window.location = wv.settings.redirectURL;
                    return;
                }

                userConfirmationManager.onTrainingEnd();

            };

            wv.promptWindow3dObject.changeText("Experience Timeout: the experience has ended due to inactivity. How would you like to proceed?", "Restart", "End");

            wv.promptWindow3dObject.askNow(onConfirmation);
        }
    }
};
